import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Bell, MessageCircle, AtSign, Heart, Facebook, Instagram, Loader2, Search,
  RefreshCw, CornerDownRight, UserPlus, Link2, Send, UserCog, ExternalLink, Check,
} from 'lucide-react';

const KIND_META = {
  comment: { label: 'Comment', icon: MessageCircle, color: 'text-blue-600' },
  tag: { label: 'Tag / Mention', icon: AtSign, color: 'text-purple-600' },
  mention: { label: 'Mention', icon: AtSign, color: 'text-purple-600' },
  reaction: { label: 'Reaction', icon: Heart, color: 'text-pink-600' },
};

const fmt = (t) => {
  if (!t) return '';
  try {
    const d = new Date(t);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

function NotificationCard({ n, users, onReply, onAssign, onCreateLead, onOpenLink, busyId, replyOpenId, setReplyOpenId }) {
  const [reply, setReply] = useState('');
  const [tagCommenter, setTagCommenter] = useState(false);
  const isIG = n.platform === 'instagram';
  const km = KIND_META[n.kind] || KIND_META.comment;
  const KIcon = km.icon;
  const canReply = n.kind === 'comment' || n.kind === 'mention' || n.kind === 'tag';
  const isReaction = n.kind === 'reaction';

  return (
    <div data-testid={`notif-${n.id}`} className="border-b border-border/60 px-4 py-3 hover:bg-muted/30 transition-colors">
      <div className="flex items-start gap-3">
        <div className="relative flex-shrink-0 mt-0.5">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            {isIG ? <Instagram className="w-4 h-4 text-pink-500" /> : <Facebook className="w-4 h-4 text-blue-600" />}
          </div>
          <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-background flex items-center justify-center ${km.color}`}>
            <KIcon className="w-3 h-3" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{n.from_name || (isIG ? 'Instagram' : 'Facebook')}</span>
            <Badge variant="secondary" className="text-[10px] py-0 h-4">{km.label}</Badge>
            {!n.read && n.direction === 'inbound' && <span className="w-2 h-2 bg-blue-500 rounded-full" data-testid={`notif-unread-${n.id}`} />}
            <span className="text-[10px] text-muted-foreground">{fmt(n.created_time)}</span>
          </div>
          {n.post_message && <p className="text-[11px] text-muted-foreground italic mt-0.5 truncate">on: {n.post_message}</p>}
          <p className="text-sm mt-1 whitespace-pre-wrap break-words">{n.text}</p>

          {/* customer + assignment chips */}
          {!isReaction && (
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {n.customer_id ? (
              <button
                data-testid={`notif-customer-${n.id}`}
                onClick={() => window.open(`/customers/${n.customer_id}`, '_blank')}
                className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5"
              >
                <Link2 className="w-3 h-3" /> {n.customer_name}{n.customer_link_auto ? ' (auto)' : ''} <ExternalLink className="w-2.5 h-2.5" />
              </button>
            ) : (
              <button
                data-testid={`notif-link-${n.id}`}
                onClick={() => onOpenLink(n)}
                className="inline-flex items-center gap-1 text-[11px] text-primary border border-dashed border-primary/40 rounded-full px-2 py-0.5 hover:bg-primary/5"
              >
                <Link2 className="w-3 h-3" /> Link to customer
              </button>
            )}
            {n.assigned_user_name ? (
              <span className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`notif-assigned-${n.id}`}>
                <UserCog className="w-3 h-3" /> {n.assigned_user_name}
              </span>
            ) : null}
            <Select onValueChange={(uid) => onAssign(n, uid)}>
              <SelectTrigger className="h-6 w-auto text-[11px] px-2 gap-1 border-dashed" data-testid={`notif-assign-${n.id}`}>
                <UserCog className="w-3 h-3" /><SelectValue placeholder={n.assigned_user_name ? 'Reassign' : 'Assign'} />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {/* actions */}
          {!isReaction && (
          <div className="flex items-center gap-3 mt-2">
            {n.permalink && (
              <a href={n.permalink} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 underline">
                View on {isIG ? 'Instagram' : 'Facebook'}
              </a>
            )}
            {canReply && !n.replied && (
              <button data-testid={`notif-reply-btn-${n.id}`} onClick={() => setReplyOpenId(replyOpenId === n.id ? null : n.id)} className="text-[11px] text-primary font-medium hover:underline">Reply</button>
            )}
            {n.replied && <span className="text-[11px] text-green-600 flex items-center gap-0.5"><Check className="w-3 h-3" />Replied{n.reply_by ? ` by ${n.reply_by}` : ''}</span>}
            {n.lead_customer_id ? (
              <span className="text-[11px] text-blue-600 flex items-center gap-0.5" data-testid={`notif-lead-done-${n.id}`}><UserPlus className="w-3 h-3" />Lead created</span>
            ) : (
              <button data-testid={`notif-create-lead-${n.id}`} onClick={() => onCreateLead(n)} disabled={busyId === n.id} className="text-[11px] text-primary font-medium hover:underline flex items-center gap-0.5 disabled:opacity-50">
                <UserPlus className="w-3 h-3" /> Create lead
              </button>
            )}
          </div>
          )}

          {replyOpenId === n.id && (
            <div className="mt-2 space-y-1.5">
              <div className="flex items-end gap-2">
                <Input
                  data-testid={`notif-reply-input-${n.id}`}
                  autoFocus placeholder="Write a public reply..."
                  value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onReply(n, reply, tagCommenter, () => { setReply(''); }); } }}
                  className="h-8 text-sm"
                />
                <Button size="sm" className="h-8" data-testid={`notif-reply-send-${n.id}`} disabled={!reply.trim() || busyId === n.id} onClick={() => onReply(n, reply, tagCommenter, () => setReply(''))}>
                  {busyId === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
              {n.platform === 'facebook' && n.from_id && (
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={tagCommenter} onChange={(e) => setTagCommenter(e.target.checked)} data-testid={`notif-tag-commenter-${n.id}`} />
                  <AtSign className="w-3 h-3" /> Tag {n.from_name} in the reply
                </label>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SocialNotifications() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [kind, setKind] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [replyOpenId, setReplyOpenId] = useState(null);
  // link customer dialog
  const [linkNotif, setLinkNotif] = useState(null);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);

  const fetchNotifs = useCallback(async () => {
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (platform !== 'all') params.platform = platform === 'messenger' ? 'facebook' : platform;
      if (search) params.search = search;
      const { data } = await api.get('/messaging/notifications', { params });
      setNotifs(data.notifications || []);
      setCounts(data.counts || {});
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  }, [kind, platform, search]);

  useEffect(() => {
    setLoading(true);
    fetchNotifs().finally(() => setLoading(false));
    api.get('/admin/users').then((r) => setUsers(r.data || [])).catch(() => {});
  }, [fetchNotifs]);

  useEffect(() => {
    const t = setInterval(fetchNotifs, 12000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/messaging/sync');
      toast.success(`Synced ${data.synced_comments || 0} notification(s) from Meta`);
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleReply = async (n, text, tagCommenter, done) => {
    if (!text.trim()) return;
    setBusyId(n.id);
    try {
      await api.post(`/messaging/notifications/${n.id}/reply`, { text: text.trim(), tag_commenter: tagCommenter });
      toast.success('Reply posted');
      setReplyOpenId(null);
      done && done();
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reply');
    } finally {
      setBusyId(null);
    }
  };

  const handleAssign = async (n, userId) => {
    try {
      const { data } = await api.post(`/messaging/notifications/${n.id}/assign`, { user_id: userId });
      toast.success(`Assigned to ${data.assigned_user_name}`);
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleCreateLead = async (n) => {
    setBusyId(n.id);
    try {
      const { data } = await api.post(`/messaging/notifications/${n.id}/create-lead`);
      toast.success(data.message || 'Lead created', {
        action: { label: 'View', onClick: () => navigate(`/customers/${data.customer_id}`) },
        duration: 10000,
      });
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setBusyId(null);
    }
  };

  // link customer dialog
  useEffect(() => {
    if (!linkNotif) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/customers', { params: { search: custSearch, limit: 8 } });
        setCustResults(Array.isArray(data) ? data : (data.customers || []));
      } catch { setCustResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [custSearch, linkNotif]);

  const doLink = async (customer) => {
    try {
      await api.post(`/messaging/notifications/${linkNotif.id}/link-customer`, { customer_id: customer.id });
      toast.success(`Linked to ${customer.first_name} ${customer.last_name}`);
      setLinkNotif(null);
      setCustSearch('');
      fetchNotifs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
  };

  const KTAB = [
    { v: 'all', label: 'All' },
    { v: 'comment', label: `Comments${counts.comment ? ` (${counts.comment})` : ''}` },
    { v: 'mentions', label: `Tags & Mentions${(counts.tag || 0) + (counts.mention || 0) ? ` (${(counts.tag || 0) + (counts.mention || 0)})` : ''}` },
    { v: 'reaction', label: `Reactions${counts.reaction ? ` (${counts.reaction})` : ''}` },
  ];

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="social-notifications-page">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2"><Bell className="w-5 h-5" /> Social Notifications</h1>
            <p className="text-xs text-muted-foreground">Comments, tags, mentions & reactions from Facebook & Instagram</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="notif-sync-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} /> Sync
          </Button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-border flex flex-col lg:flex-row lg:items-center gap-2">
          <Tabs value={kind} onValueChange={setKind} className="w-full lg:w-auto">
            <TabsList className="h-8">
              {KTAB.map((k) => <TabsTrigger key={k.v} value={k.v} className="text-xs" data-testid={`ktab-${k.v}`}>{k.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2 lg:ml-auto">
            <Tabs value={platform} onValueChange={setPlatform}>
              <TabsList className="h-8">
                <TabsTrigger value="all" className="text-xs" data-testid="ptab-all">All</TabsTrigger>
                <TabsTrigger value="facebook" className="text-xs" data-testid="ptab-facebook"><Facebook className="w-3 h-3 mr-1" />FB</TabsTrigger>
                <TabsTrigger value="instagram" className="text-xs" data-testid="ptab-instagram"><Instagram className="w-3 h-3 mr-1" />IG</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input data-testid="notif-search" placeholder="Search..." className="pl-8 h-8 text-sm w-44" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </div>

        {/* List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : notifs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <Bell className="w-10 h-10 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-md">
                Comments, tags/mentions and reactions on your Facebook & Instagram posts appear here.
                Facebook works now; Instagram appears once its capability is approved on your Meta app.
              </p>
            </div>
          ) : (
            notifs.map((n) => (
              <NotificationCard
                key={n.id} n={n} users={users}
                onReply={handleReply} onAssign={handleAssign} onCreateLead={handleCreateLead}
                onOpenLink={(nn) => { setLinkNotif(nn); setCustSearch(''); setCustResults([]); }}
                busyId={busyId} replyOpenId={replyOpenId} setReplyOpenId={setReplyOpenId}
              />
            ))
          )}
        </ScrollArea>
      </div>

      {/* Link to customer dialog */}
      <Dialog open={!!linkNotif} onOpenChange={(o) => !o && setLinkNotif(null)}>
        <DialogContent data-testid="notif-link-dialog">
          <DialogHeader><DialogTitle>Link to CRM customer</DialogTitle></DialogHeader>
          <Input autoFocus placeholder="Search customers by name…" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="notif-link-search" />
          <div className="max-h-72 overflow-y-auto divide-y">
            {custResults.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Type to search customers</p>
            ) : custResults.map((c) => (
              <button key={c.id} onClick={() => doLink(c)} data-testid={`notif-link-result-${c.id}`} className="w-full text-left px-2 py-2 hover:bg-muted flex items-center justify-between">
                <span className="text-sm">{c.first_name} {c.last_name}</span>
                <span className="text-xs text-muted-foreground">{c.phone || c.email || c.status}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
